import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPrice } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PencilSimple, Trash, CheckCircle, Archive, Plus } from "@phosphor-icons/react";

const STATUS_STYLES = {
  published: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  archived: "bg-gray-100 text-gray-500",
};

export default function AdminListings() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");

  async function load() {
    const params = status ? { status } : {};
    const { data } = await api.get("/admin/listings", { params });
    setItems(data.items);
  }
  useEffect(() => { load(); }, [status]);

  async function publish(id) {
    await api.post(`/listings/${id}/publish`);
    toast.success("Published");
    load();
  }
  async function archive(id) {
    await api.post(`/listings/${id}/archive`);
    toast.success("Archived");
    load();
  }
  async function del(id) {
    if (!window.confirm("Delete this listing permanently?")) return;
    await api.delete(`/listings/${id}`);
    toast.success("Deleted");
    load();
  }

  return (
    <div className="p-10" data-testid="admin-listings">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Inventory</div>
          <h1 className="font-serif text-4xl">Listings</h1>
        </div>
        <Link to="/admin/listings/new" className="flex items-center gap-2 bg-[#001588] text-white px-5 py-3 text-sm hover:bg-[#001588] hover:text-black transition-colors" data-testid="listings-new">
          <Plus size={16} /> New listing
        </Link>
      </div>

      <div className="flex gap-2 mb-6">
        {["", "published", "pending", "archived"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            data-testid={`status-filter-${s || "all"}`}
            className={`px-4 py-2 text-sm border transition-colors ${status === s ? "border-[#050d19] bg-[#001588] text-white" : "border-gray-300 text-gray-700 hover:border-black"}`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
      </div>

      <div className="border border-[#E5E7EB] bg-white">
        <table className="w-full text-sm" data-testid="listings-table">
          <thead className="bg-gray-50 border-b border-[#E5E7EB]">
            <tr className="text-left uppercase text-xs tracking-wider text-gray-500">
              <th className="px-4 py-3">Ref</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Community</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {items.map((l) => (
              <tr key={l.id}>
                <td className="px-4 py-3 text-gray-500 text-xs">{l.reference}</td>
                <td className="px-4 py-3 font-medium">{l.title}</td>
                <td className="px-4 py-3 text-gray-600">{l.community}</td>
                <td className="px-4 py-3 capitalize text-gray-600">{l.listing_type}</td>
                <td className="px-4 py-3">{fmtPrice(l.price, l.currency)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${STATUS_STYLES[l.status] || ""}`}>{l.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/admin/listings/${l.id}/edit`} className="p-2 hover:bg-gray-100" title="Edit" data-testid={`edit-${l.id}`}>
                      <PencilSimple size={16} />
                    </Link>
                    {user.role === "admin" && l.status !== "published" && (
                      <button onClick={() => publish(l.id)} className="p-2 hover:bg-emerald-100 text-emerald-600" title="Publish" data-testid={`publish-${l.id}`}>
                        <CheckCircle size={16} />
                      </button>
                    )}
                    {user.role === "admin" && l.status === "published" && (
                      <button onClick={() => archive(l.id)} className="p-2 hover:bg-gray-100" title="Archive" data-testid={`archive-${l.id}`}>
                        <Archive size={16} />
                      </button>
                    )}
                    {user.role === "admin" && (
                      <button onClick={() => del(l.id)} className="p-2 hover:bg-red-100 text-red-600" title="Delete" data-testid={`delete-${l.id}`}>
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">No listings found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
